import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { X, Cookie, Settings, Check } from "lucide-react";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookies, setCookies] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 2000);
    } else {
      // Load existing preferences
      const savedPreferences = JSON.parse(consent);
      setCookies(savedPreferences);
      
      // Initialize analytics if consented
      if (savedPreferences.analytics) {
        initializeAnalytics();
      }
    }
  }, []);

  const initializeAnalytics = () => {
    // Enable Google Analytics if consented
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const updateAnalyticsConsent = (granted) => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': granted ? 'granted' : 'denied',
        'ad_storage': granted ? 'granted' : 'denied',
        'ad_user_data': granted ? 'granted' : 'denied',
        'ad_personalization': granted ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    const newCookies = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setCookies(newCookies);
    localStorage.setItem('cookie-consent', JSON.stringify(newCookies));
    initializeAnalytics();
    updateAnalyticsConsent(true);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const newCookies = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    setCookies(newCookies);
    localStorage.setItem('cookie-consent', JSON.stringify(newCookies));
    updateAnalyticsConsent(false);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(cookies));
    
    if (cookies.analytics) {
      initializeAnalytics();
      updateAnalyticsConsent(true);
    } else {
      updateAnalyticsConsent(false);
    }
    
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleToggleCookie = (type) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    
    setCookies(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      {!showSettings && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-blue-600 shadow-2xl z-50 p-4 md:p-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">
                    🍪 Wir verwenden Cookies
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Wir nutzen Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                    Dazu gehören notwendige Cookies für die Funktionalität und optionale Cookies für Analysen 
                    und Verbesserungen. Sie können Ihre Einstellungen jederzeit anpassen.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="flex items-center gap-2 text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  <Settings className="h-4 w-4" />
                  Einstellungen
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Nur notwendige
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                >
                  <Check className="h-4 w-4" />
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Cookie className="h-6 w-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Cookie-Einstellungen</h2>
                </div>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Cookie Categories */}
              <div className="space-y-6">
                {/* Necessary Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg text-gray-900">Notwendige Cookies</h3>
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      Immer aktiv
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden. 
                    Sie ermöglichen die Navigation und Nutzung der Website-Funktionen.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg text-gray-900">Analyse-Cookies</h3>
                    <button
                      onClick={() => handleToggleCookie('analytics')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        cookies.analytics ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          cookies.analytics ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, 
                    um die Benutzererfahrung zu verbessern. Daten werden anonymisiert erfasst.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg text-gray-900">Marketing-Cookies</h3>
                    <button
                      onClick={() => handleToggleCookie('marketing')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        cookies.marketing ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          cookies.marketing ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Diese Cookies werden verwendet, um Werbung anzuzeigen, die für Sie und Ihre Interessen 
                    relevant ist. Sie werden derzeit nicht verwendet.
                  </p>
                </div>

                {/* Preferences Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg text-gray-900">Präferenz-Cookies</h3>
                    <button
                      onClick={() => handleToggleCookie('preferences')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        cookies.preferences ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          cookies.preferences ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Diese Cookies ermöglichen es der Website, sich an Ihre Auswahl zu erinnern 
                    und Ihnen erweiterte, personalisiertere Funktionen anzubieten.
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Nur notwendige
                </Button>
                <Button
                  onClick={handleSaveSettings}
                  className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                >
                  <Check className="h-4 w-4" />
                  Einstellungen speichern
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Alle akzeptieren
                </Button>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  Weitere Informationen finden Sie in unserer{' '}
                  <a href="/datenschutz" className="text-blue-600 hover:underline">
                    Datenschutzerklärung
                  </a>
                  {' '}und unseren{' '}
                  <a href="/agb" className="text-blue-600 hover:underline">
                    AGB
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;